import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useConsumer } from "../../context/ConsumerAuthContext";
import { useBilling } from "../../context/ConsumerBillingContext";
import { NeuralField } from "../../components/NeuralField";
import { Check, ArrowLeft, Sparkles } from "lucide-react";
import { toast } from "sonner";

const per = (p) => (p.period === "lifetime" ? " (lifetime)" : " / month");
export const planFeatureList = (p) => [
  p.manual === null ? "Unlimited manual creations" : `${p.manual} manual creation${p.manual === 1 ? "" : "s"}${per(p)}`,
  p.ai === null ? "Unlimited AI creations" : `${p.ai} AI creation${p.ai === 1 ? "" : "s"}${per(p)}`,
  ...(p.features || []),
  `${p.seats} seat${p.seats === 1 ? " (just you)" : "s"}`,
];
export const PLAN_NAME = { starter: "Starter", pro: "Plus", business: "Pro" };

export default function ConsumerPlans() {
  const { user, refresh } = useConsumer();
  const { openSubscribe, me, plans } = useBilling();
  const nav = useNavigate();
  const [interval, setInterval] = useState("month");
  const [busy, setBusy] = useState(null);
  const mustChoose = user && user.plan_selected === false;

  useEffect(() => { if (user) refresh(); }, []); // eslint-disable-line

  const choose = async (plan) => {
    setBusy(plan);
    try {
      const ok = await openSubscribe(plan, interval);
      if (ok) nav("/app");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start plan"); }
    finally { setBusy(null); }
  };

  const price = (p) => {
    const plan = plans?.[p];
    if (!plan) return "";
    if (p === "starter") return "$0";
    return interval === "year" ? `$${plan.price_year}/yr` : `$${plan.price_month}/mo`;
  };

  const current = me?.plan || user?.plan;

  return (
    <div className="relative min-h-screen w-full bg-[#05070d] text-white" data-testid="consumer-plans">
      <NeuralField tone="neon" className="fixed inset-0 w-full h-full opacity-[0.14] pointer-events-none" />
      <div className="relative z-10 max-w-5xl mx-auto px-6 py-10">
        {!mustChoose && <button onClick={() => nav("/app")} data-testid="plans-back" className="text-white/60 hover:text-white flex items-center gap-1.5 text-[13px] mb-6"><ArrowLeft size={16} /> Back</button>}

        <p className="text-cyan-300 text-[12px] uppercase tracking-[0.24em] mb-2 flex items-center gap-1.5"><Sparkles size={13} /> {mustChoose ? "Welcome — choose your plan" : "Plans & pricing"}</p>
        <h1 className="font-editorial text-4xl sm:text-5xl tracking-tight mb-6">Pick the plan that fits.</h1>

        <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-white/[0.05] border border-white/10 mb-8" data-testid="interval-toggle">
          {[{ k: "month", l: "Monthly" }, { k: "year", l: plans?.pro?.price_month ? `Annual · save ~${Math.max(0, Math.round((1 - plans.pro.price_year / (plans.pro.price_month * 12)) * 100))}%` : "Annual" }].map((o) => (
            <button key={o.k} onClick={() => setInterval(o.k)} data-testid={`interval-${o.k}`} className={`px-4 h-9 rounded-lg text-[12.5px] font-medium transition-colors ${interval === o.k ? "bg-white/10 text-white" : "text-white/50 hover:text-white/80"}`}>{o.l}</button>
          ))}
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {plans && ["starter", "pro", "business"].map((p) => {
            const isCurrent = me?.plan_selected && current === p;
            const featured = p === "pro";
            return (
              <div key={p} data-testid={`plan-${p}`} className={`relative rounded-2xl border p-6 flex flex-col ${featured ? "border-cyan-400/50 bg-cyan-400/[0.05]" : "border-white/10 bg-white/[0.03]"}`}>
                {featured && <span className="absolute -top-2.5 left-6 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full text-[#05070d] font-semibold" style={{ background: "linear-gradient(90deg,#22d3ee,#8b5cf6)" }}>Most popular</span>}
                <h3 className="font-display font-semibold text-[19px]">{plans[p].name}</h3>
                <div className="mt-2 mb-1"><span className="text-3xl font-editorial">{price(p)}</span></div>
                <div className="text-[11.5px] text-white/45 mb-4" data-testid={`plan-term-${p}`}>{plans[p].term}</div>
                <ul className="space-y-2 flex-1 mb-5">
                  {planFeatureList(plans[p]).map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-[12.5px] text-white/70"><Check size={14} className="text-cyan-300 mt-0.5 shrink-0" /> {f}</li>
                  ))}
                </ul>
                <button
                  onClick={() => choose(p)}
                  disabled={busy === p || isCurrent}
                  data-testid={`choose-${p}`}
                  className={`h-11 rounded-xl text-[13px] font-semibold flex items-center justify-center gap-2 disabled:opacity-60 ${featured ? "text-[#05070d]" : "text-white border border-white/15 hover:bg-white/5"}`}
                  style={featured ? { background: "linear-gradient(90deg,#22d3ee,#8b5cf6)" } : {}}>
                  {isCurrent ? "Current plan" : busy === p ? "Starting…" : p === "starter" ? "Choose Starter" : `Choose ${plans[p].name}`}
                </button>
              </div>
            );
          })}
        </div>

        <p className="text-[12px] text-white/40 mt-8">Need just one more? À la carte: <span className="text-white/70">$10 per manual creation</span>, <span className="text-white/70">$25 per AI creation</span> — offered automatically when you hit a limit.</p>
      </div>
    </div>
  );
}
